import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { TemplateToSaveDatabaseI } from '@interfaces/template-hsm-message.interface';
import { environment } from '@environments/environment';
import { UserHelperService } from '@helpers/user-helper.service';
import { PageI } from '@interfaces/page.interface';
import { Session360DialogI } from '@interfaces/session-360dialog.interface';
import { TemplateTypeEnum } from '@interfaces/template-hsm.interface';
import { BehaviorSubject, switchMap, Observable, tap, firstValueFrom } from '@node_modules/rxjs';
import { TemplatesSocketService } from '@events/templates/templates-socket.service';
import { TeamsI } from '@interfaces/teams.interface';

@Injectable({
  providedIn: 'root',
})
export class TemplateHsmService {
  private slug_url = 'template-hsm';

  constructor(private http: HttpClient, private userHelperService: UserHelperService, private injector: Injector) {}

  public initOnce() {
    const templatesSocketService = this.injector.get(TemplatesSocketService);
    if (!templatesSocketService) {
      return;
    }
    templatesSocketService.init();
    templatesSocketService.change$.subscribe((change) => {
      if (!change) {
        return;
      }
      this.deleteCacheData();
    });
  }

  private deleteCacheData() {
    this.templatesCacheSubject.next(null);
  }

  createTemplate(template: TemplateToSaveDatabaseI, bspKey?: string): Observable<any> {
    return this.http.post<any>(`${environment.OMNISMART_API_CHAT_PERSISTENCE}${this.slug_url}`, { template, bspKey });
  }

  getTemplates(
    search = '',
    sort = 'desc',
    page = 1,
    limit = 999,
    active = 'createdAt',
    type = 'hsm',
    teamIds: string[] = []
  ): Observable<PageI<TemplateToSaveDatabaseI>> {
    const userLogged = this.userHelperService.getUserAttributes();
    let params = new HttpParams()
      .set('profiles', userLogged['cognito:groups'])
      .set('search', search)
      .set('sort', sort)
      .set('active', active)
      .set('limit', limit.toString())
      .set('page', page.toString())
      .set('type', type);

    if (teamIds?.length) {
      params = params.set('teamIds', teamIds.join(','));
    }

    return this.http.get<PageI<TemplateToSaveDatabaseI>>(
      `${environment.OMNISMART_API_CHAT_PERSISTENCE}${this.slug_url}`,
      {
        params,
      }
    );
  }

  getDeletedTemplates(
    search = '',
    sort = 'desc',
    page = 1,
    limit = 999,
    active = 'createdAt',
    type = 'hsm'
  ): Observable<PageI<TemplateToSaveDatabaseI>> {
    const userLogged = this.userHelperService.getUserAttributes();
    const params = new HttpParams()
      .set('profiles', userLogged['cognito:groups'])
      .set('search', search)
      .set('sort', sort)
      .set('active', active)
      .set('limit', limit.toString())
      .set('page', page.toString())
      .set('type', type)
      .set('deleted', 'true');

    return this.http.get<PageI<TemplateToSaveDatabaseI>>(
      `${environment.OMNISMART_API_CHAT_PERSISTENCE}${this.slug_url}`,
      {
        params,
      }
    );
  }

  private templatesCacheSubject = new BehaviorSubject<{ data: TemplateToSaveDatabaseI[] } | null>(null);

  get templateCacheValue(): { data: TemplateToSaveDatabaseI[] } | null {
    return this.templatesCacheSubject.value;
  }
  public get templatesCache$() {
    return this.templatesCacheSubject.asObservable();
  }

  getAllTemplates(queryParams?: {
    type?: TemplateTypeEnum;
    onlyAllowedTypes?: boolean;
  }): Observable<{ data: Array<TemplateToSaveDatabaseI> }> {
    let params = new HttpParams();
    if (queryParams) {
      for (const key in queryParams) {
        const value = queryParams[key];
        if (value) {
          params = params.set(key, value);
        }
      }
    }

    if (queryParams?.type || queryParams?.onlyAllowedTypes) {
      return this.http.get<{ data: Array<TemplateToSaveDatabaseI> }>(
        `${environment.OMNISMART_API_CHAT_PERSISTENCE}${this.slug_url}/find-all?status=approved`,
        { params }
      );
    }

    return this.templatesCache$.pipe(
      switchMap((value) => {
        if (!value) {
          return this.http
            .get<{ data: Array<TemplateToSaveDatabaseI> }>(
              `${environment.OMNISMART_API_CHAT_PERSISTENCE}${this.slug_url}/find-all?status=approved`,
              { params }
            )
            .pipe(tap((value: { data: TemplateToSaveDatabaseI[] }) => this.templatesCacheSubject.next(value)));
        }
        return this.templatesCache$ as Observable<{ data: TemplateToSaveDatabaseI[] }>;
      })
    );
  }

  getSessionFromContactAndChannel(channel: string, contact: string): Observable<Session360DialogI> {
    return this.http.get<Session360DialogI>(
      `${environment.OMNISMART_API_CHAT_PERSISTENCE}${this.slug_url}/has-session-360/${channel}/${contact}`
    );
  }

  getTemplateById(id: string): Observable<TemplateToSaveDatabaseI> {
    return this.http.get<TemplateToSaveDatabaseI>(
      `${environment.OMNISMART_API_CHAT_PERSISTENCE}${this.slug_url}/${id}`
    );
  }

  getCountTemplates(): Observable<number> {
    return this.http.get<number>(`${environment.OMNISMART_API_CHAT_PERSISTENCE}${this.slug_url}/count`);
  }

  updateTemplate(_id: string, template: TemplateToSaveDatabaseI, bspKey?: string): Observable<TemplateToSaveDatabaseI> {
    let params = new HttpParams();
    if (bspKey) {
      params = params.set('bspKey', bspKey);
    }
    return this.http.put<TemplateToSaveDatabaseI>(
      `${environment.OMNISMART_API_CHAT_PERSISTENCE}${this.slug_url}/${_id}`,
      template,
      { params }
    );
  }

  syncTemplatesFrom(id: string, bspKey: string): Observable<TemplateToSaveDatabaseI[]> {
    return this.http.post<TemplateToSaveDatabaseI[]>(
      `${environment.OMNISMART_API_CHAT_PERSISTENCE}${this.slug_url}/sync`,
      { id, bspKey }
    );
  }

  updateTemplate360Dialog(
    _id: string,
    template: TemplateToSaveDatabaseI,
    bspKey: string
  ): Observable<TemplateToSaveDatabaseI> {
    const params = new HttpParams().set('_id', _id).set('bspKey', bspKey);
    return this.http.put<TemplateToSaveDatabaseI>(
      `${environment.OMNISMART_API_CHAT_PERSISTENCE}${this.slug_url}/360dialog`,
      template,
      { params }
    );
  }

  delete(_id: string, bspKey?: string): Observable<void> {
    if (bspKey) {
      const params = new HttpParams().set('bspKey', bspKey);
      return this.http.delete<void>(`${environment.OMNISMART_API_CHAT_PERSISTENCE}${this.slug_url}/${_id}`, { params });
    }

    return this.http.delete<void>(`${environment.OMNISMART_API_CHAT_PERSISTENCE}${this.slug_url}/${_id}`);
  }

  saveMedia(channelId: string, file) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<any>(
      `${environment.OMNISMART_API_CHAT_PERSISTENCE}${this.slug_url}/save-media/${channelId}`,
      formData
    );
  }

  public syncTemplates(customerId: string): Observable<{
    success: boolean;
    details: {
      updated: number;
      deleted: number;
    };
  }> {
    return this.http.post<{
      success: boolean;
      details: {
        updated: number;
        deleted: number;
      };
    }>(`${environment.OMNISMART_API_WHATSAPP_OFICIAL}${this.slug_url}/sync`, { customerId });
  }

  private buildExportParams(filters: { search?: string; sort?: string; type?: string }): HttpParams {
    let params = new HttpParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, value.toString());
      }
    });
    return params;
  }

  public exportCsv(filters: { search?: string; sort?: string; type?: string }) {
    return firstValueFrom(
      this.http.get<{ jobId: string }>(
        `${environment.OMNISMART_API_ATTENDANCE_REPORTS}template-list-export/csv/exports`,
        { params: this.buildExportParams(filters) }
      )
    );
  }

  public exportExcel(filters: { search?: string; sort?: string; type?: string }) {
    return firstValueFrom(
      this.http.get<{ jobId: string }>(
        `${environment.OMNISMART_API_ATTENDANCE_REPORTS}template-list-export/excel/exports`,
        { params: this.buildExportParams(filters) }
      )
    );
  }

  public async getAllApprovedTemplatesHsmByTeam(team: TeamsI): Promise<TemplateToSaveDatabaseI[]> {
    const data = (await firstValueFrom(this.getAllTemplates({ onlyAllowedTypes: true }))) as any;
    return (
      data?.data?.filter((template) => {
        if (team?.templatesHsm?.length) {
          return (
            template.template.status === 'approved' &&
            template.type === TemplateTypeEnum.HSM &&
            team?.customer === template.customer &&
            team.templatesHsm.includes(template._id)
          );
        }
        return (
          team?.customer === template.customer &&
          template.template.status === 'approved' &&
          template.type === TemplateTypeEnum.HSM
        );
      }) ?? []
    );
  }
}
